// Стоп-кран в вебе (033: T012; FR-007 – FR-009; ASG-NTF-11; contracts/api.md, «Стоп-кран»): общее
// для полосы «Все сообщения остановлены» (shell/StoppedBanner.tsx), «Опасной зоны» раздела
// «Рассылка», краткой панели «Состояние» и раздела «Почта». Состояние — delivery.safety.get:
// один запрос в кэше на всех, фоновый опрос раз в 60 с, как колокольчик (NotificationsFeed): он
// не продлевает сеанс (FR-008б 001). «Остановить» и «Возобновить» — только через окно
// подтверждения с числом писем в очереди (ux.md «Табеля», раздел 1); ответ — новое состояние
// в кэш, ошибка — тост с кодом и «Повторить», окно остаётся открытым.
import { mailStopRoleLabels } from '@kp/contracts';
import type { MailSafety } from '@kp/contracts';
import { ConfirmDialog } from '@kp/ui';
import { CirclePlay, OctagonPause } from '@kp/ui/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { RefObject } from 'react';
import { api, apiKey, apiQuery } from '../../../api/client.ts';
import { useErrorToast } from '../../../app/errors.ts';
import { formatDateTime } from '../../../app/format.ts';
import { can } from '../../../shell/permissions.ts';
import { useMe } from '../../../shell/session.ts';

/** Опрос стоп-крана: SC-005 — полоса видна не позже следующего обновления, до 60 с. */
export const SAFETY_POLL_MS = 60_000;

/** Ключ кэша состояния стоп-крана: ответы «Остановить» и «Возобновить» кладутся сюда. */
export const SAFETY_KEY = apiKey('delivery.safety.get');

/** Запрос состояния: фоновый, раз в минуту; сеанс не продлевает. */
export function safetyQuery() {
  return {
    ...apiQuery('delivery.safety.get', { background: true }),
    refetchInterval: SAFETY_POLL_MS,
    staleTime: 30_000,
  };
}

/** Состояние стоп-крана у того, кому оно доступно (mail.stop); у остальных запроса нет. */
export function useMailSafety() {
  const me = useMe();
  return useQuery({ ...safetyQuery(), enabled: can(me.permissions, ['mail.stop']) });
}

/** «01.11 14:05»: день и месяц без года — полоса короткая, остановка — дело дней, а не лет. */
export function formatDayMonthTime(iso: string, timeZone: string): string {
  return formatDateTime(iso, timeZone).replace(/^(\d{2}\.\d{2})\.\d{4}/, '$1');
}

/** «01.11 14:05 · контролёр» — когда и кто остановил, роль без ФИО (FR-009). */
export function stoppedWhen(stopped: NonNullable<MailSafety['stopped']>, timeZone: string): string {
  return `${formatDayMonthTime(stopped.at, timeZone)} · ${mailStopRoleLabels[stopped.role]}`;
}

/**
 * «Остановить» или «Возобновить»: ответ сервера — новое состояние в кэш; письма в «Письмах» ждут
 * или уходят — их список перечитывается. Ошибка — тост с кодом и «Повторить»; окно не закрывается.
 */
export function useSafetySwitch(): (stop: boolean) => Promise<void> {
  const queryClient = useQueryClient();
  const showError = useErrorToast();
  const run = async (stop: boolean): Promise<void> => {
    try {
      const next = await api(stop ? 'delivery.safety.stop' : 'delivery.safety.resume', {
        body: {},
      });
      queryClient.setQueryData(SAFETY_KEY, next);
    } catch (error) {
      showError(error, () => void run(stop).catch(() => undefined));
      throw error;
    }
    void queryClient.invalidateQueries({ queryKey: ['api', 'delivery.messages.list'] });
  };
  return run;
}

/**
 * Окно «Остановить все сообщения?» или «Возобновить все сообщения?» (ux.md «Табеля», раздел 1):
 * без крестика, вопрос, последствия числом писем в очереди, «Отмена» в фокусе, кнопка «глагол +
 * объект». Остановка обратима (есть «Возобновить») — окно янтарное, главная кнопка не красная.
 */
export function SafetyDialog({
  action,
  open,
  onOpenChange,
  queued,
  onDone,
  returnFocusRef,
}: {
  action: 'stop' | 'resume';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Писем в очереди — из последнего ответа delivery.safety.get. */
  queued: number;
  /** После успешного действия, до закрытия окна: куда вернуть фокус, если кнопки больше нет. */
  onDone?: () => void;
  returnFocusRef?: RefObject<HTMLElement | null>;
}) {
  const run = useSafetySwitch();
  const stop = action === 'stop';
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      tone="warn"
      icon={stop ? OctagonPause : CirclePlay}
      title={stop ? 'Остановить все сообщения?' : 'Возобновить все сообщения?'}
      description={
        stop
          ? `Не уйдёт ни одно сообщение ни в одном канале: письма о пунктах, ссылки входа, восстановление пароля, приглашения и письма о доступе. В очереди: ${String(queued)} — они и новые будут ждать, пока сообщения не возобновят. Изменение запишется в журнал действий`
          : `В очереди: ${String(queued)} — письма уйдут по очереди, новые — по мере появления. Ссылки входа и восстановления пароля с истёкшим сроком не уйдут. Изменение запишется в журнал действий`
      }
      confirmLabel={stop ? 'Остановить все сообщения' : 'Возобновить сообщения'}
      onConfirm={async () => {
        await run(stop);
        onDone?.();
      }}
      returnFocusRef={returnFocusRef}
    />
  );
}
